import { useEffect, useState } from 'react';
import { useCallbackRef } from './callback-ref.js';
import { useSingleton } from './singleton.js';

/**
 * inspired by Reacts MutableRefObject, this method will create an observable
 * ref that allows subscribing callbacks that will be triggered whenever the
 * value changes
 *
 * this observability provides powerful features to allow integrations to easily
 * manage side effects and derived state in the background, while always keeping
 * the current value available in all closures
 */
function createObservableRef(initialValue) {
  const observers = new Set();
  const observableRef = {
    current: initialValue,
    observe(callback) {
      // creating a wrapped callback makes sure that the observer is unique so
      // that if the same callback is added twice, it will be run twice
      //
      // the alternative would result in unpredictable behaviour when multiple
      // subscriptions are performed, for instance the following scenario would
      // result in a complete unsubscription:
      //
      // const disconnect1 = observe(callback);
      // const disconnect2 = observe(callback);
      // disconnect1(); // even tho disconnect2 hasn't been called, all subscriptions are removed
      const wrappedCallback = () => callback();
      observers.add(wrappedCallback);
      return () => {
        observers.delete(wrappedCallback);
      };
    }
  };

  // create the "current" property with the ability to trigger list of observers
  // whenever value changes
  let currentValue = initialValue;
  Object.defineProperty(observableRef, "current", {
    enumerable: true,
    configurable: false,
    set(nextValue) {
      const hasChanged = currentValue !== nextValue;
      currentValue = nextValue;
      if (hasChanged) {
        // using Array.from to duplicate the set of observers prior to execution
        // in order to avoid infinite loops in case on observer will result in a
        // new subscription being made
        for (const observer of [...observers]) {
          observer();
        }
      }
    },
    get() {
      return currentValue;
    }
  });
  return observableRef;
}

/**
 * abstraction to create observable refs inside React components, following the
 * same signature as the built-in useRef hook
 */
function useObservableRef(initialValue) {
  return useSingleton(() => createObservableRef(initialValue));
}

/**
 * abstraction to create a value derived from multiple observable refs
 *
 * the value is exposed as a read-only observable ref, so that it is not ever
 * changed externally; it is meant to be an auto-updated value that is derived
 * from the given list of dependencies
 */
function useDerivedObservableRef(depRef, selector) {
  const observableRef = useSingleton(() => createObservableRef(selector(depRef.current)));
  const selectorRef = useCallbackRef(selector);
  useEffect(() => {
    observableRef.current = selectorRef(depRef.current);
    return depRef.observe(() => {
      observableRef.current = selectorRef(depRef.current);
    });
  }, [depRef, observableRef, selectorRef]);
  return observableRef;
}

/**
 * abstraction to unbox an observable ref, and return it's value as a React
 * state so that changes will trigger re-rendering of the component
 */

function useObservedValue(observableRef, selector = current => current) {
  const [state, setState] = useState(() => selector(observableRef.current));
  const selectorRef = useCallbackRef(selector);
  useEffect(() => {
    setState(selectorRef(observableRef.current));
    return observableRef.observe(() => {
      setState(selectorRef(observableRef.current));
    });
  }, [observableRef, selectorRef]);
  return state;
}

export { createObservableRef, useDerivedObservableRef, useObservableRef, useObservedValue };
//# sourceMappingURL=observable-ref.js.map
